import { DetailPage, Row } from "../../components/crud/DetailPage"
import { ListPage } from "../../components/crud/ListPage"
import { AsyncSelect, DateInput, EmailInput, Select, TextareaInput, TextInput } from "../../components/forms/Inputs"
import { useOptions } from "../../hooks/useOptions"
import { useTranslations } from "../../layers/Localization"
import { ChantiersList, useTypeOptions } from "./Chantiers"
import { ContactsList } from "./Contacts"
import { FacturesList } from "./Factures"
import { useLangueOptions } from "./Gerants"
import { PlaintesList } from "./Plaintes"
import { useEffect, useState } from "react"
import { HistoriquesList } from "./Historique"
import { useApi } from "../../hooks/useApi"
import { useParams } from "react-router-dom"
import { postalCodeToCommune } from "../../hooks/helpers"
import { useToday } from "../../hooks/useToday"
import { useSociete } from "../../hooks/useSociete"

export const usePaysOptions = () => {
    const t = useTranslations()
    return [
        { value: 'BE', label: t('BE') },
        { value: 'FR', label: t('FR') },
        { value: 'PT', label: t('PT') },
    ]
}

export const useRegimeTvaOptions = () => {
    const t = useTranslations()
    return [
        { value: 'NON_ASSUJETTI_NORMAL', label: t('Non assujetti (tva normale)'), codeTva: 'VINGT_ET_UN' },
        { value: 'ASSUJETTI_NORMAL', label: t('Assujetti (tva normale)'), codeTva: 'VINGT_ET_UN' },
        { value: 'ASSUJETTI_COCONTRACTANT_AUTOLIQUIDATION', label: t('Assujetti cocontractant (autoliquidation)'), codeTva: 'VINGT_BIS' },
        { value: 'ASSUJETTI_INTRACOMMUNAUTAIRE_AUTOLIQUIDATION', label: t('Assujetti intracommunautaire (autoliquidation)'), codeTva: 'VINGT_BIS' },
        { value: 'ASSUJETTI_HORS_EU_EXEMPTE', label: t('Assujetti hors EU (exempté)'), codeTva: 'ZERO' },
        { value: 'EXONERE_EXEMPTE', label: t('Exonéré (exempté)'), codeTva: 'ZERO' },
    ]
}

const useActions = () => {
    const t = useTranslations()
    const societe = useSociete()
    return {
        [t("Ouvrir check-in")]: { href: item => societe?.checkInFolder ? 'file:///' + societe.checkInFolder.replaceAll('{nom}', (item.referenceCourte || '').replaceAll(' ', '_')) : null },
    }
}

function ClientFields({ item, set, tab, originalItem }) {
    const [chantiers, setChantiers] = useState([])
    const t = useTranslations()
    const gerantOptions = useOptions('gerants', ['nom', 'type'])
    const typeOptions = useTypeOptions()
    const paysOptions = usePaysOptions()
    const regimeTvaOptions = useRegimeTvaOptions()
    const langueOptions = useLangueOptions()

    const api = useApi()
    const { id } = useParams()

    useEffect(() => {
        if (!item.id && !item.paysPopsy && item.numTva && paysOptions.map(x => x.value).includes(item.numTva.slice(0, 2))) set('paysPopsy')(item.numTva.slice(0, 2))
    }, [item.id, item.paysPopsy, item.numTva])

    useEffect(() => {
        if (id !== 'new') api.get(`/chantiers?clientId=${encodeURIComponent(id)}`).then(setChantiers)
    }, [id])

    const openChantiers = chantiers.filter(x => !x.dateFin)
    const invalidLastDate = item.dateFin && openChantiers.length && t("Impossible de fermer le client car il reste des chantiers non fermés: {chantiers}", { chantiers: openChantiers.map(x => x.nom).join(', ') })

    const onCodePostalChange = value => {
        set('cp')(value)
        set('commune')(postalCodeToCommune(value))
    }
    const onGerantChange = (value, item) => {
        set('gerantId')(value)
        set('emailFacturation')(item?.emailFacturation || '')
        set('emailRappels')(item?.emailRappels || '')
    }
    const onTypeChange = value => {
        set('type')(value)
        if (id === 'new' && value) {
            const typeOption = typeOptions.find(x => x.value === value)
            api.get(`/clients?limit=1&orderBy:desc=num&num:gte=${encodeURIComponent(typeOption.minNum)}&num:lte=${encodeURIComponent(typeOption.maxNum)}`).then(clients => set('num')(String(clients[0] ? Number(clients[0].num) + 1 : typeOption.minNum).padStart(5, '0')))
        }
    }
    const onNumTvaChange = value => {
        set('numTva')(value)
        if (id === 'new' && value && paysOptions.map(x => x.value).includes(value.slice(0, 2))) set('paysPopsy')(value.slice(0, 2))
    }

    return <>
        {tab === t('General') && <>
            <Row>
                <Select label={t("Type")} value={item.type} onChange={onTypeChange} options={typeOptions} required />
                <TextInput label={t("Numéro")} value={item.num} onChange={set('num')} required max="999999" />
                <TextInput className="grow" label={t("Référence courte")} value={item.referenceCourte} onChange={set('referenceCourte')} required />
                <Select label={t("Langue")} value={item.langue} onChange={set('langue')} options={langueOptions} required />
            </Row>
            <Row>
                <TextInput className="grow" label={t("Nom du client")} value={item.nom} onChange={set('nom')} required />
                <TvaInput label={t("Numéro de TVA")} value={item.numTva} onChange={onNumTvaChange} />
            </Row>
            <Row>
                <TextInput className="grow" label={t("Adresse du client")} value={item.adresse} onChange={set('adresse')} />
                <TextInput label={t("Code postal")} value={item.cp} onChange={onCodePostalChange} />
                <TextInput label={t("Commune")} value={item.commune} onChange={set('commune')} />
            </Row>
            <Row>
                <AsyncSelect label={t("c/o Gérant")} value={item.gerantId} onChange={onGerantChange} options={gerantOptions} add="/gerants/new" />
            </Row>
            <hr />
            <Row>
                <DateInput label={t("Date de premier travail")} value={item.datePremierTravail} onChange={set('datePremierTravail')} />
                <DateInput label={t("Date de dernier travail")} value={item.dateDernierTravail} onChange={set('dateDernierTravail')} invalid={invalidLastDate} />
            </Row>
            {item.dateDernierTravail && originalItem && !originalItem.dateDernierTravail && <div className="text-red-600 font-bold">{t("N'oubliez pas de clôturer la déclaration de travaux et le checking !")}</div>}
            <TextareaInput className="[&_textarea]:text-red-600" label={t("Mémo interne")} value={item.memoInterne} onChange={set('memoInterne')} />
            <TextareaInput label={t("Mémo livraison")} value={item.memoLivraison} onChange={set('memoLivraison')} />
        </>}
        {tab === t('Facturation') && <>
            <Row>
                <Select label={t("Régime TVA")} value={item.regimeTva} onChange={set('regimeTva')} required options={regimeTvaOptions} />
                <Select label={t("Pays Popsy")} value={item.paysPopsy} onChange={set('paysPopsy')} options={paysOptions} required />
            </Row>
            <Row>
                <EmailInput className="grow" label={t("Email facturation")} value={item.emailFacturation} onChange={set('emailFacturation')} required />
                <EmailInput className="grow" label={t("Email rappels")} value={item.emailRappels} onChange={set('emailRappels')} required />
            </Row>
        </>}
    </>
}

export function ClientsList(props) {
    const t = useTranslations()
    const typeOptions = useTypeOptions()
    const actions = useActions()
    const today = useToday()

    return <>
        <ListPage model="clients" title={t("Clients & Prospects")} columns={{
            [t('Référence courte')]: 'referenceCourte',
            [t('Numéro')]: 'num',
            [t('Nom')]: 'nom',
            [t('Code postal')]: 'cp',
            [t('Type')]: { sort: 'type', value: item => typeOptions.find(option => option.value === item.type)?.label },
            [t('Numéro de TVA')]: 'numTva',
        }} join={{
            gerant: 'nom'
        }} filter={{
            [t('Actifs')]: `or2:datePremierTravail:lte=${encodeURIComponent(today.toISOString())}&or2:datePremierTravail=&or3:dateDernierTravail:gt=${encodeURIComponent(today.toISOString())}&or3:dateDernierTravail=`,
            [t('Passés')]: `dateDernierTravail:lte=${encodeURIComponent(today.toISOString())}`,
            [t('Futurs')]: `datePremierTravail:gte=${encodeURIComponent(today.toISOString())}`,
            [t('Tous')]: ``,
        }} actions={actions} mainActions={{
            [t('Exporter vers popsy')]: reload => window.open(`${process.env.REACT_APP_API_URL}/clients/export-popsy`),
        }} {...props} />
    </>
}

export function ClientDetail(props) {
    const [client, setClient] = useState()
    const { id } = useParams()
    const t = useTranslations()
    const api = useApi()
    const paysOptions = usePaysOptions()
    const actions = useActions()
    const baseContactsActions = {
        [t('Transférer entre client et gérant')]: (contact, reload) => contact?.clientId
            ? api.patch(`/contacts/${contact.id}`, { gerantId: client.gerantId, clientId: null }).then(reload)
            : api.patch(`/contacts/${contact.id}`, { clientId: client.id, gerantId: null }).then(reload)
    }
    const contactsActions = {
        ...baseContactsActions,
        [t('Activer pour ce client')]: (contact, reload) => api.post(`/contacts/${contact.id}/clientsActifs/${id}`).then(reload)
    }
    const contactsActifsActions = {
        ...baseContactsActions,
        [t('Désactiver pour ce client')]: (contact, reload) => api.delete(`/contacts/${contact.id}/clientsActifs/${id}`).then(reload)
    }

    return <DetailPage model="clients" title={t("Client")} Fields={ClientFields} onItemLoad={setClient} tabs={[t('General'), t('Facturation')]} tables={{
        [t("Chantiers")]: <ChantiersList relation="clientId" />,
        [t("Contacts actifs")]: <ContactsList key={"contactsActifs" + client?.gerantId} filter={client?.gerantId ? {
            [t("Contacts gérant")]: `gerantId=${client.gerantId}&clientsActifs=${client.id}`,
            [t("Contacts client")]: `clientId=${client.id}&clientsActifs=${client.id}`,
        } : `clientId=${id}&clientsActifs=${id}`} title={undefined} actions={contactsActifsActions} root="." />,
        [t("Contacts")]: <ContactsList key={"contacts" + client?.gerantId} filter={client?.gerantId ? {
            [t("Contacts gérant")]: `gerantId=${client.gerantId}`,
            [t("Contacts client")]: `clientId=${client.id}`,
        } : `clientId=${id}`} title={undefined} actions={contactsActions} root="." />,
        [t("Historique")]: <HistoriquesList relation="clientId" root="." title={t("Historique Client & Chantiers")} />,
        [t("Plaintes")]: <PlaintesList relation="clientId" root="." title={t("Plaintes Client & Chantiers")} />,
        [t("Factures")]: <FacturesList relation="clientId" />,
    }} actions={actions} defaults={{
        paysPopsy: paysOptions[0].value
    }} {...props} />
}

export function TvaInput({ value, onChange, label, ...props }) {
    const finalOnChange = v => {
        if (value && value.length < v.length) {
            if (/^[A-Z]{2}[0-9]$/.test(v)) v = v.slice(0, 2) + " " + v.slice(2)
            if (/^BE [0-9]{5}$/.test(v)) v = v.slice(0, 7) + "." + v.slice(7)
            if (/^BE [0-9]{4}.[0-9]{4}$/.test(v)) v = v.slice(0, 11) + "." + v.slice(11)
        }
        if (value && value.length > v.length) {
            if (/[ .]$/.test(v)) v = v.slice(0, v.length - 1)
        }
        onChange(v)
    }
    return <TextInput {...props} label={label} value={value} onChange={finalOnChange} placeholder="BE 0123.456.789" pattern={"(^BE [0-9]{4}.[0-9]{3}.[0-9]{3}$)|^[A-Z]{2} ?[0-9.]+$"} spellCheck="false" />
}